import React, { FC, useState } from 'react';

import { Box, Text, useDisclosure } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import {
  formatNumberWithComma,
  renderSeverityBar,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import {
  ResourceTypeIconTooltip,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { ComplianceAddException } from 'containers/Compliance/Components/AddException/ComplianceAddException';
import { renderPassedChecks } from 'containers/Compliance/Components/ComplianceTables/utils';
import { History } from 'containers/Compliance/Components/History';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';
import { SeverityComponent } from 'containers/Compliance/Components/SeverityComponent';

import { ResourcesUnderPolicyDrawer } from './ResourcesUnderPolicyDrawer';

interface Props {
  subControlRecord?: Record<string, any>;
}

export const PoliciesTable: FC<Props> = props => {
  const { subControlRecord } = props;
  const dispatch = useDispatch();
  const { rules } = useSelector(selectCompliance);
  const {
    renderRulesActions,
    isOpenSeverityModal,
    isOpenHistory,
    isOpenSuppress,
    onCloseSuppress,
    selectedResource,
  } = useCompliance();
  const policiesDrawer = useDisclosure();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const [sortByField, setSortByField] = useState<Sorted>({
    sortField: '',
    sortOrder: '',
  });
  const { cloudNativeMapping } = useResourceType();

  const rulesColumns = [
    {
      header: (
        <Box pl={10} ml={1}>
          Description
        </Box>
      ),
      accessor: 'description',
      render: ({ value, row }) => (
        <WithResourceIcon
          resourceType={row?.native_resource}
          iconTooltip={
            <ResourceTypeIconTooltip resourceType={row?.native_resource} />
          }
          tooltip={value}
        >
          <Text whiteSpace="wrap" noOfLines={2}>
            {value}
          </Text>
        </WithResourceIcon>
      ),
    },
    {
      header: 'Resource Type',
      accessor: 'native_resource',
      align: 'left',
      render: ({ value }) => (
        <CustomTooltip label={<ResourceTypeIconTooltip resourceType={value} />}>
          <StackedCell
            upper={
              cloudNativeMapping[value]?.resource_alias ??
              cloudNativeMapping?.[value]?.cloud_native_name
            }
            lower={cloudNativeMapping[value]?.cloud_agnostic_name}
            showUpperTooltip={false}
            showLowerTooltip={false}
          />
        </CustomTooltip>
      ),
      styles: { width: '210px', cell: { maxWidth: '210px' } },
    },
    {
      header: 'Severity',
      accessor: 'severity',
      sortKey: 'severity',
      align: 'left',
      render: ({ value }) => renderSeverityBar({ value, isInline: false }),
      styles: {
        width: '80px',
        cell: { maxWidth: '80px' },
      },
    },
    {
      header: 'Entities',
      accessor: 'check_stats.total_entities',
      render: ({ value }) => formatNumberWithComma(value),
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
    {
      header: 'Passed Policies',
      accessor: 'check_stats',
      render: renderPassedChecks,
      align: 'left',
      styles: { width: '200px', cell: { maxWidth: '200px' } },
    },
    {
      header: 'Actions',
      render: renderRulesActions,
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
  ];

  return (
    <Box h="full" flex={1}>
      <Table
        data={rules.data}
        columns={rulesColumns}
        isLoading={rules.isLoading}
        onRowClick={row => {
          setCurrentRecord(row);
          policiesDrawer.onOpen();
        }}
        cursor="pointer"
        pagination={{
          totalCount: rules.page.totalCount,
          pageInfo: rules.page.info,
          onChange: pageInfo => {
            dispatch(actions.updateRulesPageInfo(pageInfo));
          },
        }}
        onSort={sortInfo => {
          if (!isEqual(sortByField, sortInfo)) {
            setSortByField(sortInfo);
          }
        }}
        sortBy={sortByField}
      />
      {isOpenSeverityModal && !policiesDrawer.isOpen && <SeverityComponent />}
      {isOpenSuppress && !policiesDrawer.isOpen && (
        <ComplianceAddException
          policy={selectedResource}
          exceptionFor="CompliancePolicy"
          isOpen={isOpenSuppress}
          onClose={onCloseSuppress}
        />
      )}
      {isOpenHistory && !policiesDrawer.isOpen && (
        <History
          control={subControlRecord?.control}
          subControl={subControlRecord?.sub_control}
          policyDescription={selectedResource?.description}
        />
      )}
      {policiesDrawer.isOpen && (
        <ResourcesUnderPolicyDrawer
          row={currentRecord}
          isOpen={policiesDrawer.isOpen}
          onClose={policiesDrawer.onClose}
          subControl={subControlRecord}
        />
      )}
    </Box>
  );
};
